
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { logger } from "@/lib/logger";

export const useUserProfile = () => {
  const { user } = useAuth();
  
  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        logger.debug('useUserProfile: No user ID available', { user });
        return null;
      }
      
      logger.debug('useUserProfile: Fetching profile for user', { 
        userId: user.id, 
        userEmail: user.email 
      });
      
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          organizations(name)
        `)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) {
        logger.error('Error fetching user profile', { 
          error: error.message, 
          code: error.code,
          details: error.details,
          hint: error.hint,
          userId: user.id 
        });
        throw error;
      }
      
      logger.debug('useUserProfile: Profile fetched successfully', { 
        profileId: data?.id,
        organizationId: data?.organization_id,
        name: data?.name,
        organizationName: data?.organizations?.name
      });
      
      return data;
    },
    enabled: !!user?.id,
    staleTime: 0, // Force fresh data
    gcTime: 0, // Don't cache for debugging (renamed from cacheTime in newer versions)
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
  
  return {
    profile,
    isLoading,
    error,
    refetch,
  };
};
